/**
 * Single-select person filter ("Todos / Eu / <dependente>") shown on the plans
 * and workouts lists when the account acts for more than one student (guardian
 * scope). Options derive from the loaded items' `studentCustomer`, so the row
 * only renders when there is actually someone to tell apart.
 */

export interface StudentFilterOption {
  id: string
  title: string
}

interface StudentFilterPillsProps {
  options: StudentFilterOption[]
  selectedId: string | null
  onSelect: (id: string | null) => void
}

export function StudentFilterPills({ options, selectedId, onSelect }: StudentFilterPillsProps) {
  if (options.length === 0) return null

  const pillClass = (isSelected: boolean) =>
    `px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
      isSelected
        ? 'bg-brand-primary text-white'
        : 'bg-background-white text-text-primary border border-border-light hover:border-brand-primary'
    }`

  return (
    <div className="flex gap-2 overflow-x-auto pb-1 mb-4">
      <button type="button" className={pillClass(selectedId === null)} onClick={() => onSelect(null)}>
        Todos
      </button>
      {options.map(option => (
        <button
          key={option.id}
          type="button"
          className={pillClass(selectedId === option.id)}
          onClick={() => onSelect(option.id)}
        >
          {option.title}
        </button>
      ))}
    </div>
  )
}

/**
 * One option per student in the items — the account holder as "Eu", dependents
 * by name. Empty unless more than one student is visible.
 */
export function buildStudentFilterOptions(
  items: Array<{ studentCustomer?: { id: string; name: string } | null; isDependent?: boolean | null }>
): StudentFilterOption[] {
  const seen = new Set<string>()
  const options: StudentFilterOption[] = []

  for (const item of items) {
    const student = item.studentCustomer
    if (!student || seen.has(student.id)) continue
    seen.add(student.id)
    options.push({ id: student.id, title: item.isDependent ? student.name : 'Eu' })
  }

  return options.length > 1 ? options : []
}
